import React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';
import { analytics } from '@/services/analytics/analyticsService';

export const useAuthProvider = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  // Track if user has been seen in this session
  const userTracked = React.useRef(false);

  // Check if user is admin
  const checkUserRole = async (userId: string) => {
    try {
      console.log('Checking admin status for user:', userId);
      
      // Check cache first for performance
      const cachedAdminStatus = localStorage.getItem('adminStatus');
      const cachedTimestamp = localStorage.getItem('adminStatusTimestamp');
      const now = Date.now();
      
      // Only use cache if it's fresh (less than 5 minutes old)
      if (cachedAdminStatus && cachedTimestamp && 
          (now - parseInt(cachedTimestamp)) < 5 * 60 * 1000) {
        const isAdminFromCache = cachedAdminStatus === 'true';
        console.log('Using cached admin status:', isAdminFromCache);
        setIsAdmin(isAdminFromCache);
        return isAdminFromCache;
      }
      
      // If no fresh cache, check with the server
      const { data, error } = await supabase.rpc('is_admin');
      
      if (error) {
        console.error('Error checking admin status:', error);
        return false;
      }
      
      console.log('Admin check result:', data);
      
      // Update cache
      localStorage.setItem('adminStatus', data ? 'true' : 'false');
      localStorage.setItem('adminStatusTimestamp', now.toString());
      
      setIsAdmin(!!data);
      return !!data;
    } catch (error) {
      console.error('Error in checkUserRole:', error);
      return false;
    }
  };

  // Clear all auth-related cache and storage
  const clearAuthCache = () => {
    try {
      console.log('Clearing auth cache and storage');
      
      // Clear specific auth items
      localStorage.removeItem('adminStatus');
      localStorage.removeItem('adminStatusTimestamp');
      sessionStorage.removeItem('googleAuthInProgress');
      sessionStorage.removeItem('authProvider');
      
      // Clear any other potential auth cache
    } catch (error) {
      console.error('Error clearing auth cache:', error);
    }
  };

  // Handle Google auth process
  const handleGoogleAuthCallback = () => {
    const googleAuthTimestamp = sessionStorage.getItem('googleAuthInProgress');
    const authProvider = sessionStorage.getItem('authProvider');
    
    if (googleAuthTimestamp || authProvider === 'google') {
      console.log('Detected return from Google auth flow');
      console.log('Timestamp:', googleAuthTimestamp);
      console.log('Provider:', authProvider);
      
      // If we have a user, we've successfully authenticated
      if (user) {
        console.log('Google authentication completed successfully for user:', user.email);
        
        // Clear the auth progress flags
        sessionStorage.removeItem('googleAuthInProgress');
        sessionStorage.removeItem('authProvider');
        
        // Navigate to dashboard after a short delay to ensure everything is loaded
        setTimeout(() => {
          navigate('/dashboard');
        }, 100);
      } else {
        console.log('Google auth in progress but no user found yet');
        
        // We'll keep the timestamp for now and let the auth callback handle it
        // It will be cleared either on success or after too many retries
      }
    }
  };

  // Determine if this is a return visit
  const checkIfReturnVisit = (userId: string) => {
    const lastVisitTimestamp = localStorage.getItem(`last_visit_${userId}`);
    const now = Date.now();
    
    // Update the last visit timestamp
    localStorage.setItem(`last_visit_${userId}`, now.toString());
    
    if (lastVisitTimestamp) {
      const lastVisit = parseInt(lastVisitTimestamp);
      const daysSinceLastVisit = Math.floor((now - lastVisit) / (1000 * 60 * 60 * 24));
      
      // If more than 1 day since last visit, it's a return visit
      if (daysSinceLastVisit >= 1) {
        analytics.trackReturnVisit(userId, daysSinceLastVisit);
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    console.log('Setting up auth state listener');
    let mounted = true;
    
    // Set up auth state listener FIRST to ensure we don't miss events
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      console.log('Auth state changed:', event, newSession ? 'Session exists' : 'No session');
      
      if (!mounted) return;
      
      if (newSession) {
        // Update the session and user state
        setSession(newSession);
        setUser(newSession.user);
        
        // Set analytics user ID
        if (newSession.user) {
          analytics.setUserId(newSession.user.id);
          
          // Track return visit if applicable (once per session)
          if (!userTracked.current) {
            checkIfReturnVisit(newSession.user.id);
            userTracked.current = true;
          }
        }
        
        // Handle sign-in event
        if (event === 'SIGNED_IN') {
          const fullName = newSession.user?.user_metadata?.name || 
                          newSession.user?.user_metadata?.full_name ||
                          'User';
          
          console.log(`User signed in: ${fullName}`);
          
          // Complete Google auth if in progress
          const googleAuthInProgress = sessionStorage.getItem('googleAuthInProgress');
          const authProvider = sessionStorage.getItem('authProvider');
          
          if (googleAuthInProgress || authProvider === 'google') {
            console.log('Completing Google auth process after SIGNED_IN event');
            sessionStorage.removeItem('googleAuthInProgress');
            sessionStorage.removeItem('authProvider');
            
            // Navigate to dashboard
            navigate('/dashboard');
          }
        }
        
        // Check admin status if we have a user
        if (newSession.user) {
          setTimeout(async () => {
            if (mounted) {
              await checkUserRole(newSession.user.id);
            }
          }, 0);
        }
        
        setIsLoading(false);
      } else {
        // Clear user data when signed out
        setSession(null);
        setUser(null);
        setIsAdmin(false);
        
        // Clear analytics user ID
        analytics.setUserId(null);
        
        // Also clear cached admin status and auth progress flags
        clearAuthCache();
        
        if (event === 'SIGNED_OUT') {
          console.log('User signed out');
          
          // Redirect to home if accessing protected route
          const currentPath = window.location.pathname;
          if (currentPath.includes('/dashboard') || currentPath.includes('/admin')) {
            navigate('/');
          }
        }
        
        setIsLoading(false);
      }
    });

    // THEN check for existing session
    const getInitialSession = async () => {
      try {
        console.log('Getting initial session');
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting initial session:', error);
          if (mounted) setIsLoading(false);
          return;
        }
        
        if (!mounted) return;
        
        if (data?.session) {
          console.log('Initial session found:', data.session.user?.email);
          setSession(data.session);
          setUser(data.session.user);
          
          if (data.session.user) {
            setTimeout(async () => {
              if (mounted) {
                await checkUserRole(data.session.user.id);
              }
            }, 0);
          }
          
          // Check if we were in the middle of a Google auth flow
          handleGoogleAuthCallback();
        } else {
          console.log('No initial session found');
          // Ensure we clear any cached admin status if no session exists
          clearAuthCache();
        }
      } catch (error) {
        console.error('Error in getInitialSession:', error);
        // Clear cache on error to be safe
        clearAuthCache();
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    getInitialSession();

    // Add a timeout to ensure isLoading is set to false
    const timeoutId = setTimeout(() => {
      if (mounted && isLoading) {
        console.log('Auth loading timed out, setting isLoading to false');
        setIsLoading(false);
      }
    }, 3000);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [navigate]);

  return {
    user,
    session,
    isLoading,
    isAdmin,
    checkUserRole,
    setUser,
    setSession,
    setIsAdmin,
    setIsLoading
  };
};
